/**
 * Utility functions for formatting data in docket search
 */

export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export const formatDateForDisplay = (dateString: string): string => {
  if (!dateString) return '';

  // Date-only strings ("YYYY-MM-DD") arrive from Postgres `date` columns
  // (e.g. the get_proceeding_filings RPC casts the timestamp to date).
  // `new Date("YYYY-MM-DD")` parses as UTC midnight, which then renders
  // as the PREVIOUS day in any negative-offset timezone (May 18 →
  // May 17 in Eastern). Treat the string as an already-calendar-day
  // value and render its components directly, with no TZ conversion.
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateString);
  if (dateOnly) {
    const [, y, m, d] = dateOnly;
    return new Date(Date.UTC(Number(y), Number(m) - 1, Number(d))).toLocaleDateString(
      'en-US',
      { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' },
    );
  }

  // Full ISO timestamps render in Eastern time so display matches the
  // FCC's filing-deadline jurisdiction (and the period-bucketing logic
  // in proceedingsFilingsUi.ts already uses America/New_York).
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'America/New_York',
  });
};

export const formatElapsedTime = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  
  if (hours > 0) {
    return `${hours}h ${minutes}m ${secs}s`;
  } else if (minutes > 0) {
    return `${minutes}m ${secs}s`;
  } else {
    return `${secs}s`;
  }
};

export const getTimeEstimate = (seconds: number): string => {
  if (seconds < 60) return 'Less than a minute';
  if (seconds < 300) return 'A few minutes';
  if (seconds < 900) return 'About 10-15 minutes';
  return 'More than 15 minutes';
};

export const isDateRangeValid = (startDate: string, endDate: string): boolean => {
  if (!startDate || !endDate) return false;
  return new Date(startDate) <= new Date(endDate);
};

export const setQuickDateRange = (days: number): { startDate: string; endDate: string } => {
  const endDate = new Date();
  const startDate = new Date();
  // Include today as part of the range (matches DocketSearchContent)
  startDate.setDate(endDate.getDate() - (days - 1));

  const toIsoDate = (d: Date) => d.toISOString().slice(0, 10);

  return {
    startDate: toIsoDate(startDate),
    endDate: toIsoDate(endDate),
  };
};

/** Default advanced date range: last N calendar days inclusive (default 365). */
export function getDefaultDateRangeInclusive(totalDaysIncluded: number = 365): {
  startDate: string;
  endDate: string;
} {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - (totalDaysIncluded - 1));
  const toIsoDate = (d: Date) => d.toISOString().slice(0, 10);
  return {
    startDate: toIsoDate(start),
    endDate: toIsoDate(end),
  };
}
